import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const InvoicePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);

  useEffect(() => {
    loadInvoiceData();
  }, [id]);

  const loadInvoiceData = async () => {
    try {
      const response = await api.get(`${API_CONFIG.INVOICES.VIEW_INVOICE}?id=${id}`);
      const data = await response.data;
      
      if (data.success) {
        setInvoice(data.invoice);
      } else {
        showToast('Invoice not found', 'error');
      }
    } catch (error) {
      showToast('Failed to load invoice', 'error');
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    setIsPaying(true);
    try {
      const response = await api.get(`${API_CONFIG.INVOICES.PAY_INVOICE}/${invoice.invoice_code}`);
      const data = await response.data;
      
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        showToast('Payment initialization failed', 'error');
      }
    } catch (error) {
      showToast('Payment processing error', 'error');
    } finally {
      setIsPaying(false);
    }
  };

  const formatAmount = (amount) => {
    return new Number(amount).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <h4 className="text-sm font-semibold  mb-2">Invoice not found</h4>
          <button
            onClick={() => navigate('/')}
            className="text-xs bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b border-gray-200 p-3">
          <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-2">
            <h4 className="text-sm font-bold  uppercase">Invoice</h4>
            <div className="text-sm font-semibold text-gray-700">{invoice.invoice_number}</div>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="p-3">
          <div className="space-y-4 text-xs">
            {/* Company Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 border-b border-gray-200 pb-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {invoice.company_logo && (
                  <img
                    src={invoice.company_logo}
                    alt="Company Logo"
                    className="w-10 h-10 object-contain flex-shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold  truncate">{invoice.company_name}</div>
                  {invoice.reg_number && (
                    <div className="text-gray-600 truncate">{invoice.reg_number}</div>
                  )}
                </div>
              </div>
              <div className="text-base font-bold  flex-shrink-0">INVOICE</div>
            </div>

            {/* Contact and Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              <div className="space-y-1">
                {invoice.company_address && (
                  <div className="text-gray-600 break-words">{invoice.company_address}</div>
                )}
                {invoice.company_phone && (
                  <div className="text-gray-600">{invoice.company_phone}</div>
                )}
                {invoice.company_email && (
                  <div className="text-gray-600 truncate">{invoice.company_email}</div>
                )}
                {invoice.company_website && (
                  <div className="text-gray-600 truncate">{invoice.company_website}</div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-medium">{invoice.issue_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice No:</span>
                  <span className="font-medium">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Due Date:</span>
                  <span className="font-medium">{invoice.due_date}</span>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="border-b border-gray-200 pb-4">
              <div className="text-sm font-semibold  mb-2">Bill To</div>
              <div className="space-y-1">
                {invoice.recipient_name && (
                  <div className="text-gray-600 font-medium">{invoice.recipient_name}</div>
                )}
                {invoice.recipient_company && (
                  <div className="text-gray-600">{invoice.recipient_company}</div>
                )}
                {invoice.recipient_phone && (
                  <div className="text-gray-600">{invoice.recipient_phone}</div>
                )}
                {invoice.recipient_email && (
                  <div className="text-gray-600 truncate">{invoice.recipient_email}</div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="border-b border-gray-200 pb-4">
              <div className="grid grid-cols-12 gap-1 mb-2 text-xs font-semibold ">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>

              <div className="space-y-2">
                {invoice.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-1 text-xs py-1 border-b border-gray-50">
                    <div className="col-span-6 text-gray-600 break-words">{item.description}</div>
                    <div className="col-span-2 text-center text-gray-600">{item.quantity}</div>
                    <div className="col-span-2 text-right text-gray-600">
                      {invoice.currency} {formatAmount(item.rate)}
                    </div>
                    <div className="col-span-2 text-right text-gray-600 font-medium">
                      {invoice.currency} {formatAmount(item.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              {/* Payment Details */}
              <div>
                <div className="text-sm font-semibold  mb-2">Payment Details</div>
                <div className="space-y-1 text-xs">
                  {invoice.bank_account && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account No:</span>
                      <span className="text-gray-600">{invoice.bank_account}</span>
                    </div>
                  )}
                  {invoice.bank_account_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Account Name:</span>
                      <span className="text-gray-600">{invoice.bank_account_name}</span>
                    </div>
                  )}
                  {invoice.bank_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bank Name:</span>
                      <span className="text-gray-600">{invoice.bank_name}</span>
                    </div>
                  )}
                  {invoice.swift_code && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Swift Code:</span>
                      <span className="text-gray-600">{invoice.swift_code}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Totals */}
              <div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal:</span>
                    <span className="text-gray-600">
                      {invoice.currency} {formatAmount(invoice.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Discount:</span>
                    <span className="text-gray-600">
                      {invoice.currency} {formatAmount(invoice.discount)}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold  border-t border-gray-200 pt-1">
                    <span>Total:</span>
                    <span>
                      {invoice.currency} {formatAmount(invoice.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Amount in Words */}
            {invoice.amount_in_words && (
              <div className="border-b border-gray-200 pb-4">
                <div className="text-sm font-semibold  mb-1">Amount In Words:</div>
                <div className="text-xs text-gray-600 break-words">{invoice.amount_in_words}</div>
              </div>
            )}

            {/* Signature */}
            {(invoice.signature || invoice.signatory_name) && (
              <div className="flex flex-col items-end gap-2">
                {invoice.signature && (
                  <img
                    src={invoice.signature}
                    alt="Signature"
                    className="h-8 object-contain"
                  />
                )}
                {invoice.signatory_name && (
                  <div className="text-xs text-gray-600">
                    {invoice.signatory_name}
                    {invoice.company_name && (
                      <span className="text-gray-500"> for {invoice.company_name}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Powered by footer */}
            <div className="text-center pt-4 border-t border-gray-200">
              <small className="text-xs text-gray-500">
                Powered by{' '}
                <a 
                  href="https://www.paymeo.co" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Weperch Technologies LLC
                </a>
              </small>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 justify-end mt-6 pt-4 border-t border-gray-200">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none text-center"
              onClick={handlePayment}
              disabled={isPaying || invoice.status === 'paid'}
            >
              {isPaying ? 'Processing...' : 'Pay Now'}
            </button>
            
            <button
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm flex-1 sm:flex-none text-center"
              onClick={() => navigate('/signup')}
            >
              Create Free Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;